import { z } from 'zod';
import { AiAdvanceValidationError } from '../../ai-platform/ports.js';
import { conversationModeSchema } from './conversation-mode.js';
import { athenaInlineUiSchema, athenaPendingConfirmSchema } from './conversation.js';
import { athenaRecommendationItemSchema } from './intelligence.js';
export const CDLIT_AI_ASSIST_OTHER_TEXT_MAX = 1500;
export const CDLIT_AI_ASSIST_REPLY_MAX = 500;
export const cdlitAiAssistObjectionTypeSchema = z.enum(['price', 'need_to_think', 'no_time']);
export const cdlitAiAssistHandoffReasonSchema = z.enum([
    'request_human',
    'safety',
    'provider_unavailable',
]);
export const cdlitAiAssistSuggestedStepSchema = z.enum([
    'zone',
    'match',
    'problem',
    'massage_goal',
    'safety',
    'recommendation',
]);
export const cdlitAiAssistOtherRequestSchema = z
    .object({
    kind: z.literal('other'),
    text: z.string().min(1).max(CDLIT_AI_ASSIST_OTHER_TEXT_MAX),
})
    .strict();
export const cdlitAiAssistObjectionRequestSchema = z
    .object({
    kind: z.literal('objection'),
    objection: cdlitAiAssistObjectionTypeSchema,
})
    .strict();
export const cdlitAiAssistHandoffRequestSchema = z
    .object({
    kind: z.literal('handoff'),
    reason: cdlitAiAssistHandoffReasonSchema,
})
    .strict();
/** Internal resolved assist request (server-side after catalog lookup). */
export const cdlitAiAssistRequestSchema = z.discriminatedUnion('kind', [
    cdlitAiAssistOtherRequestSchema,
    cdlitAiAssistObjectionRequestSchema,
    cdlitAiAssistHandoffRequestSchema,
]);
/** Public assist body — only assistOptionId (+ text when required by catalog). */
export const cdlitAiAssistPublicRequestSchema = z
    .object({
    assistOptionId: z.string().min(1).max(64),
    text: z.string().min(1).max(CDLIT_AI_ASSIST_OTHER_TEXT_MAX).optional(),
})
    .strict();
export const cdlitAiAssistLlmOutputSchema = z
    .object({
    route: z.enum(['guided_clarify', 'handoff']),
    reply: z.string().max(CDLIT_AI_ASSIST_REPLY_MAX),
    suggestedStep: cdlitAiAssistSuggestedStepSchema.nullable(),
})
    .strict();
export const cdlitAiAssistResponseRouteSchema = z.enum([
    'guided_clarify',
    'handoff',
    'continue_guided',
    'recommend',
]);
export const cdlitAiAssistResponseSourceSchema = z.enum(['ai', 'fallback', 'template']);
export const cdlitAiAssistResponseSchema = z
    .object({
    route: cdlitAiAssistResponseRouteSchema,
    reply: z.string().max(CDLIT_AI_ASSIST_REPLY_MAX),
    suggestedStep: cdlitAiAssistSuggestedStepSchema.nullable(),
    source: cdlitAiAssistResponseSourceSchema,
    /**
     * Server runtime mode (`ai` | `crm_fallback`). Required on successful public /assist responses.
     * Distinct from assist `source` (`ai` | `fallback` | `template`) — template/fallback source ≠ crm_fallback mode.
     */
    mode: conversationModeSchema,
    /** Intelligence layer — 1–3 процедуры с объяснением. */
    recommendations: z.array(athenaRecommendationItemSchema).max(3).optional(),
    /** When intelligence / legacy router advanced FSM — public session view for UI sync. */
    session: z.unknown().optional(),
    /** Internal intelligence path — surfaces on public conversation when agent awaits confirm. */
    pendingConfirm: athenaPendingConfirmSchema.optional(),
    /** Agent tool path — slots/services inline UI without FSM advance. */
    inlineUiOverride: athenaInlineUiSchema.optional(),
})
    .strict();
export const CDLIT_AI_ASSIST_FORBIDDEN_BODY_FIELDS = [
    'productKey',
    'tenantKey',
    'roleKey',
    'systemPrompt',
    'serviceId',
    'staffId',
    'price',
    'slot',
    'action',
    'payload',
    'transcript',
    'kind',
    'objection',
    'reason',
];
export function assertNoForbiddenAssistBodyFields(body) {
    if (body == null || typeof body !== 'object' || Array.isArray(body))
        return;
    for (const key of CDLIT_AI_ASSIST_FORBIDDEN_BODY_FIELDS) {
        if (key in body) {
            throw new AiAdvanceValidationError(`Field ${key} is not allowed`);
        }
    }
}
//# sourceMappingURL=assist.js.map